import { isPageAccessible } from '@eptai/eptai-app-components';

import { useAppAuth, useFeatureFlags } from '../contexts';
import { useNavigation } from '../contexts/NavigationContext';

// 'dr1' is the Deal Rooms page id in the shared navigation permissions.
const DEAL_ROOMS_PAGE_ID = 'dr1';

// ============================================================
// private
// ============================================================

/**
 * Same role check as home landing (`shouldLandOnRoomsList`).
 *
 * Example: `['AI Sales Pro']` -> true; `['Admin']` -> false.
 */
function _hasAiSalesProRole(roles: readonly string[] | null | undefined): boolean {
  return Boolean(roles?.includes('AI Sales Pro'));
}

// ============================================================
// public
// ============================================================

/**
 * Determines whether the current user may view the Rooms list and detail pages.
 *
 * Access is granted when:
 * - the user has the `AI Sales Pro` Auth0 role (same rule as home landing), OR
 * - the `application_room_enabled` feature flag is on, OR
 * - the user has Deal Rooms page access (`dr1`) via mapped nav roles.
 *
 * AI Sales Pro is checked from raw Auth0 `ept_roles` first so RoomsAccessGuard
 * cannot bounce them to `/ai-chat` after HomeRoute sends them to rooms when
 * `workspace_enabled` is off (e.g. while feature flags are resolving or nav
 * roles are briefly empty). When that flag is on, `/` lands on `/workspace`
 * instead; rooms access is unchanged.
 *
 * A missing `ept_roles` claim means "no roles", not "still loading": Auth0
 * populates the claim together with the user, so users whose token omits it
 * would otherwise wait on a claim that never arrives.
 *
 * Example: flag off + raw roles `['AI Sales Pro']` -> `{ canAccess: true, isLoading: false }`.
 * Example: flag off + roles `['Admin']` -> `{ canAccess: false }` once flags finish loading.
 */
export function useCanAccessRooms(): { canAccess: boolean; isLoading: boolean } {
  const { user, isLoading: authLoading } = useAppAuth();
  const { hasFlag, isLoading: flagsLoading } = useFeatureFlags();
  const { userRoles } = useNavigation();

  const rawRoles = (user as { ept_roles?: string[] } | null | undefined)?.ept_roles;

  const canAccess =
    _hasAiSalesProRole(rawRoles) ||
    _hasAiSalesProRole(userRoles) ||
    hasFlag('application_room_enabled') ||
    isPageAccessible(userRoles, DEAL_ROOMS_PAGE_ID);

  // Once access is established, never hold the guard on a spinner.
  return { canAccess, isLoading: canAccess ? false : authLoading || flagsLoading };
}
